import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import type { LocalGatewayHandoffResponse } from "@/types/api";
import type { IssuedLocalGatewayHandoffPollingOptions } from "./hooks/useStorages";

interface RememberedHandoff {
  handoff: LocalGatewayHandoffResponse;
  pollingOptions: IssuedLocalGatewayHandoffPollingOptions;
}

interface HandoffMemory {
  read: (storageId: string) => RememberedHandoff | null;
  remember: (handoff: LocalGatewayHandoffResponse, options?: IssuedLocalGatewayHandoffPollingOptions) => void;
  forget: (storageId: string) => void;
}

const LocalGatewayHandoffContext = createContext<HandoffMemory | null>(null);

// The key destroys display-only secrets when authentication or membership changes.
export function AuthenticatedLocalGatewayHandoffProvider({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const membership = user?.currentMembership;
  const scope = !isLoading && user && membership
    ? `${user.accountId}:${membership.customerId}:${membership.customerUserId}`
    : "unauthenticated";
  return (
    <LocalGatewayHandoffProvider key={scope}>
      {children}
    </LocalGatewayHandoffProvider>
  );
}

export function LocalGatewayHandoffProvider({ children }: { children: ReactNode }) {
  const entries = useRef(new Map<string, RememberedHandoff>());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const active = useRef(true);
  const memory = useMemo<HandoffMemory>(() => {
    const forget = (storageId: string) => {
      clearTimeout(timers.current.get(storageId));
      timers.current.delete(storageId);
      entries.current.delete(storageId);
    };
    const read = (storageId: string) => {
      const entry = entries.current.get(storageId);
      if (!active.current || !entry) return null;
      if (Date.parse(entry.handoff.expiresAt) <= Date.now()) {
        forget(storageId);
        return null;
      }
      return entry;
    };
    const expire = (storageId: string) => {
      const entry = read(storageId);
      if (!entry) return;
      // Long fixture / upstream deadlines must not overflow the browser timer.
      timers.current.set(storageId, setTimeout(() => expire(storageId),
        Math.min(Date.parse(entry.handoff.expiresAt) - Date.now(), 2_147_483_647)));
    };
    return {
      read,
      forget,
      remember(handoff, pollingOptions = {}) {
        if (!active.current || !handoff.storageId) return;
        forget(handoff.storageId);
        const expiresAt = Date.parse(handoff.expiresAt);
        if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) return;
        entries.current.set(handoff.storageId, {
          handoff: { ...handoff },
          pollingOptions: {
            purpose: pollingOptions.purpose,
            initialActiveRouteCount: pollingOptions.initialActiveRouteCount,
            initialRouteIds: pollingOptions.initialRouteIds
              ? [...pollingOptions.initialRouteIds] : undefined,
          },
        });
        expire(handoff.storageId);
      },
    };
  }, []);

  useEffect(() => {
    active.current = true;
    const currentEntries = entries.current;
    const currentTimers = timers.current;
    return () => {
      active.current = false;
      currentTimers.forEach(clearTimeout);
      currentTimers.clear();
      currentEntries.clear();
    };
  }, []);

  return <LocalGatewayHandoffContext.Provider value={memory}>{children}</LocalGatewayHandoffContext.Provider>;
}

export function useLocalGatewayHandoffMemory() {
  const memory = useContext(LocalGatewayHandoffContext);
  if (!memory) throw new Error("local_gateway_handoff_provider_missing");
  return memory;
}
